import styled from "@emotion/styled"
import { Event } from "@mui/icons-material"
import {
  FormControl,
  FormHelperText,
  Icon,
  InputAdornment,
  InputLabel,
  Typography,
  TypographyProps,
} from "@mui/material"
import * as api from "api"
import { CommonForm } from "common/forms"
import { addDays, format } from "date-fns"
import { he } from "date-fns/locale"
import { Field } from "formik"

import { Select, TextField } from "formik-mui"
import Shared from "theme/shared"
import { ServiceCenterContactStatus } from "../dialogs/ServiceCenterContactDialog"
import schema, { initialFormState, treatmentTypes } from "./TreatmentFormSchema"

import { LocalizationProvider } from "@mui/x-date-pickers"
// If you are using date-fns v3.x, please import the v3 adapter
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3"
import { getPrivacyPolicyLink } from "common/NavigationLinks"
import { DatePicker } from "formik-mui-x-date-pickers"
import Link from "next/link"
import { useEffect, useState } from "react"

const DATE_FORMAT = "dd/MM/yyyy"

const Styled = {
  FormContainer: styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
  Field: styled(Field)``,
  TypeWrap: styled.div`
    position: relative;
  `,
  Counter: styled.span`
    position: absolute;
    left: 5px;
    bottom: 7px;
    font-size: 0.75rem;
  `,
  SendButton: styled(Shared.Button)`
    height: 35px;
    font-size: 14px;
    width: auto;
    width: 150px;
    white-space: nowrap;
    margin-top: 16px;
  `,
  PrivacyPolicyCaption: styled((props: TypographyProps) => (
    <Typography variant="body2" {...props} />
  ))``,
  Link: styled(Link)`
    text-decoration: underline;
  `,
}

type TreatmentFormProps = {
  onChange: (status: ServiceCenterContactStatus) => void
  ServiceCenter: api.SingleServiceCenter & api.ServiceCenter
}

const TreatmentForm = ({ onChange, ServiceCenter }: TreatmentFormProps) => {
  const today = new Date()
  const day = today.getDay()
  const minDate = addDays(new Date(), day > 3 || day < 2 ? 2 : 7 - day)
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState<string>("")

  useEffect(() => {
    getPrivacyPolicyLink().then((link) => {
      setPrivacyPolicyLink(link)
    })
  }, [])

  return (
    <CommonForm
      validationSchema={schema}
      initialValues={initialFormState}
      onSubmit={async (values) => {
        try {
          const { data } = await api.postServiceCenterContact({
            name: values.fullName,
            mobilePhone: values.phone,
            serviceCenterId: ServiceCenter.id,
            applicationType: "Treatment",
            rishui: values.licenceNumber,
            email: ServiceCenter.email,
            customerEmail: null,
            content: null,
            treatmentDate: format(values.treatmentDate, DATE_FORMAT),
            treatmentTime: "",
            treatmentType: values.treatmentType,
          })
          onChange(data.body ? "success" : "failure")
        } catch (error) {
          console.error(error)
          onChange("failure")
        }
      }}
      render={({ errors, touched }) => (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
          <Styled.FormContainer>
            <Field
              name="fullName"
              component={TextField}
              label="שם מלא"
              autoComplete="name"
              id="name"
              required
              helperText=" " // prevent a "jump" when an error appears
            />
            <Field
              name="phone"
              id="phone"
              component={TextField}
              type="tel"
              label="מספר נייד"
              autoComplete="tel-national"
              required
              helperText=" " // prevent a "jump" when an error appears
              inputProps={{ maxLength: 11 }}
            />
            <Field
              name="licenceNumber"
              id="licenceNumber"
              component={TextField}
              label="לוחית רישוי"
              type="tel"
              required
              placeholder="000-00-000"
              helperText=" " // prevent a "jump" when an error appears
              inputProps={{ maxLength: 10 }}
            />
            <Styled.TypeWrap>
              <FormControl variant="outlined">
                <InputLabel
                  htmlFor="treatmentType"
                  required
                  style={{ background: "#fff", padding: "0 5px" }}
                >
                  סוג טיפול
                </InputLabel>

                <Field
                  component={Select}
                  name="treatmentType"
                  id="treatmentType"
                  variant="outlined"
                  native
                  aria-describedby="treatmentType-helper-text"
                >
                  {treatmentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Field>
              </FormControl>

              {errors.treatmentType && touched.treatmentType && (
                <FormHelperText error id="treatmentType-helper-text">
                  {errors.treatmentType}
                </FormHelperText>
              )}
            </Styled.TypeWrap>
            <Field
              component={DatePicker}
              variant="inline"
              inputVariant="outlined"
              label="תאריך לטיפול"
              name="treatmentDate"
              format={DATE_FORMAT}
              minDate={minDate}
              // minutesStep={30}
              // views={["date", "hours"]}
              placeholder={format(minDate, DATE_FORMAT)}
              required
              autoOk
              disableToolbar
              disablePast
              helperText=" " // prevent a "jump" when an error appears
              shouldDisableDate={(date) =>
                date.getDay() === 5 || date.getDay() === 6
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position={"end"}>
                    <Icon>
                      <Event />
                    </Icon>
                  </InputAdornment>
                ),
              }}
            />
            <Styled.PrivacyPolicyCaption>
              מסירת הפרטים מהווה הסכמה{" "}
              <Styled.Link target="_blank" href={privacyPolicyLink}>
                למדיניות הפרטיות
              </Styled.Link>{" "}
              של דלק מוטורס
            </Styled.PrivacyPolicyCaption>

            {/* TODO: Use LoadingButton */}
            <Styled.SendButton
              variant="contained"
              color="primary"
              type="submit"
            >
              שליחת פרטים
            </Styled.SendButton>
          </Styled.FormContainer>
        </LocalizationProvider>
      )}
    />
  )
}

export default TreatmentForm
